import { api } from '@/lib/api/axios';
import type { Company } from '@/types';
import type {
  CompanyCityDto,
  CompanySlugAvailabilityDto,
  CompanyStateDto,
  RegisterCompanyDto,
  UpdateCompanyBookingSettingsDto,
  UpdateCompanyDto,
} from '@/types/company.dto';

function pickString(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }

  return '';
}

function normalizeStateDto(state: unknown): CompanyStateDto {
  const record = state && typeof state === 'object' ? (state as Record<string, unknown>) : {};

  return {
    id: pickString(record, ['id', '_id', 'stateId']),
    name: pickString(record, ['name', 'nome']),
    stateCode: pickString(record, ['stateCode', 'uf', 'code', 'sigla', 'abbreviation']),
  };
}

function normalizeCityDto(city: unknown): CompanyCityDto {
  const record = city && typeof city === 'object' ? (city as Record<string, unknown>) : {};
  const nestedState = record.state ?? record.uf ?? record.stateInfo;

  return {
    id: pickString(record, ['id', '_id', 'cityId']),
    name: pickString(record, ['name', 'nome']),
    state:
      nestedState && typeof nestedState === 'object'
        ? normalizeStateDto(nestedState)
        : {
            id: pickString(record, ['stateId']),
            name: pickString(record, ['stateName']),
            stateCode: pickString(record, ['stateCode', 'uf']),
          },
  };
}

export function resolveActiveCompanyPayload(
  payload: Company | { company: Company } | Company[] | null | undefined,
): Company | null {
  if (!payload || Array.isArray(payload)) {
    return null;
  }

  if (typeof payload === 'object' && 'company' in payload) {
    return payload.company;
  }

  return payload;
}

export const CompanyService = {
  list: () => api.get<Company[]>('/company'),
  get: (id: string) => api.get(`/company/${id}`),
  getMyCompany: () =>
    api.get<Company | { company: Company } | Company[]>(`/company`, {
      validateStatus: (status) => (status >= 200 && status < 300) || status === 401 || status === 404,
    }),
  register: (data: RegisterCompanyDto) => api.post('/company', data),
  update: (data: UpdateCompanyDto) => api.patch<Company>('/company', data),
  listStates: async () => {
    const response = await api.get<unknown[]>('/geography/states');
    return {
      ...response,
      data: Array.isArray(response.data) ? response.data.map(normalizeStateDto) : [],
    };
  },
  listCities: async (params: { stateId: string; search?: string }) => {
    const response = await api.get<unknown[]>('/geography/cities', { params });
    return {
      ...response,
      data: Array.isArray(response.data) ? response.data.map(normalizeCityDto) : [],
    };
  },
  checkSlugAvailability: (slug: string) =>
    api.get<CompanySlugAvailabilityDto>('/company/slug-availability', {
      params: { slug },
    }),
  uploadPhoto: (photoKind: 'profile' | 'cover', file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post<Company>(`/company/${photoKind}-photo`, formData);
  },
  updateBookingSettings: (data: UpdateCompanyBookingSettingsDto) =>
    api.patch<Company>('/company/booking-settings', data),
  finishOnboarding: () => api.post('/company/finish-onboarding'),
};
